import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Search, Trophy, Users, AlertCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  listUpcomingMatches,
  listTeams,
  setMatchVisibility,
  setTeamHidden,
  MatchRow,
  TeamRow,
} from '../services/visibility';
import { supabase } from '../lib/supabase';

type Tab = 'matches' | 'teams';

export function VisibilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab) === 'teams' ? 'teams' : 'matches';
  const [tab, setTab] = useState<Tab>(initialTab);

  // Filtres venant de /sync : competition pour matchs, sport pour équipes
  const competitionFilter = searchParams.get('competition') || '';
  const sportFilter = searchParams.get('sport') || '';

  const switchTab = (next: Tab) => {
    setTab(next);
    // Garde competition si on switch matchs↔équipes
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const clearFilter = () => {
    setSearchParams({ tab }, { replace: true });
  };

  const hasActiveFilter = competitionFilter || sportFilter;

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <EyeOff size={20} />
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Visibilité</h1>
      </div>
      <p className="muted" style={{ marginBottom: '20px', fontSize: '13px' }}>
        Cache des matchs ou des équipes individuels (en plus du toggle compétition dans /sync).
        Tout ce qui est masqué disparaît instantanément de l'app.
      </p>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <TabBtn active={tab === 'matches'} onClick={() => switchTab('matches')} icon={<Trophy size={14} />}>
          Matchs
        </TabBtn>
        <TabBtn active={tab === 'teams'} onClick={() => switchTab('teams')} icon={<Users size={14} />}>
          Équipes
        </TabBtn>

        {hasActiveFilter && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 10px', borderRadius: '999px',
            background: 'var(--accent-dim, rgba(48,209,88,0.12))',
            border: '1px solid var(--accent, #30D158)',
            color: 'var(--accent, #30D158)',
            fontSize: '12px', fontWeight: 600,
          }}>
            <span>
              Filtré : {competitionFilter || sportFilter}
            </span>
            <button
              onClick={clearFilter}
              title="Retirer le filtre"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'inherit', display: 'flex', padding: 0,
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {tab === 'matches'
        ? <MatchesTab presetSearch={competitionFilter} />
        : <TeamsTab sportFilter={sportFilter} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '8px 14px',
        fontSize: '13px', fontWeight: 600,
        borderRadius: '999px',
        border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
        background: active ? 'var(--accent)' : 'var(--bg-secondary)',
        color: active ? '#FFFFFF' : 'var(--text-muted)',
        cursor: 'pointer',
        transition: 'all 0.1s',
      }}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── Matchs tab ───
function MatchesTab({ presetSearch }: { presetSearch?: string }) {
  const [rows, setRows] = useState<MatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(presetSearch || '');
  const [savingId, setSavingId] = useState<string | null>(null);

  // Si la query string change (ex : switch compétition depuis /sync), refresh la search
  useEffect(() => {
    setSearch(presetSearch || '');
  }, [presetSearch]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listUpcomingMatches());
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const ch = supabase.channel('vis-matches-rt')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'matches' }, () => reload())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [reload]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const s = search.toLowerCase();
    return rows.filter(m =>
      m.home_team.toLowerCase().includes(s) ||
      m.away_team.toLowerCase().includes(s) ||
      m.competition.toLowerCase().includes(s)
    );
  }, [rows, search]);

  const handleToggle = async (m: MatchRow) => {
    setSavingId(m.id);
    const next = !m.is_visible;
    try {
      await toast.promise(setMatchVisibility(m, next), {
        loading: 'Sauvegarde…',
        success: next ? 'Match visible' : 'Match masqué',
        error: 'Erreur — vérifie les permissions',
      });
      // Optimistic
      setRows(prev => prev.map(r => r.id === m.id ? { ...r, is_visible: next } : r));
    } catch {} finally {
      setSavingId(null);
    }
  };

  const visibleCount = rows.filter(r => r.is_visible).length;
  const hiddenCount = rows.length - visibleCount;

  return (
    <>
      <div style={{ display: 'flex', gap: '20px', marginBottom: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
        <span><b style={{ color: 'var(--text-primary)' }}>{rows.length}</b> matchs à venir</span>
        <span style={{ color: 'var(--green)' }}><b>{visibleCount}</b> visibles</span>
        <span style={{ color: 'var(--red)' }}><b>{hiddenCount}</b> masqués</span>
      </div>

      <div className="filter-search" style={{ marginBottom: '14px', maxWidth: '380px' }}>
        <Search size={14} />
        <input placeholder="Rechercher équipe / compétition…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {error && <ErrorRow msg={error} />}
      {loading ? <Skeleton /> : filtered.length === 0 ? (
        <Empty msg="Aucun match" />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {filtered.map((m, idx) => (
            <div
              key={m.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 16px',
                borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                opacity: m.is_visible ? 1 : 0.55,
                transition: 'opacity 0.15s',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{m.home_team} vs {m.away_team}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {format(new Date(m.match_date + 'T00:00:00'), 'EEE d MMM', { locale: fr })}
                  {m.match_time && ` · ${m.match_time}`}
                  {' · '}{m.competition}
                </div>
              </div>
              <Toggle
                value={m.is_visible}
                disabled={savingId === m.id}
                onChange={() => handleToggle(m)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Équipes tab ───
function TeamsTab({ sportFilter }: { sportFilter?: string }) {
  const [rows, setRows] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // La search est vide au mount — l'utilisateur tape un nom d'équipe précis.
  // Le sportFilter (param URL depuis /sync) limite déjà à foot ou rugby.
  const [search, setSearch] = useState('');
  const [savingName, setSavingName] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listTeams());
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const ch = supabase.channel('vis-teams-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'hidden_teams' }, () => reload())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [reload]);

  const filtered = useMemo(() => {
    let result = rows;
    // Filtre par sport (param URL depuis /sync)
    if (sportFilter && sportFilter.trim()) {
      const sp = sportFilter.toLowerCase();
      result = result.filter(t => (t.sport || '').toLowerCase().includes(sp));
    }
    // Filtre par recherche texte (nom équipe)
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(t => t.team_name.toLowerCase().includes(s));
    }
    return result;
  }, [rows, search, sportFilter]);

  const handleToggle = async (t: TeamRow) => {
    setSavingName(t.team_name);
    const hide = !t.is_hidden;
    try {
      await toast.promise(setTeamHidden(t, hide), {
        loading: 'Sauvegarde…',
        success: hide ? `${t.team_name} masquée` : `${t.team_name} réaffichée`,
        error: 'Erreur — vérifie les permissions',
      });
      setRows(prev => prev.map(r => r.team_name === t.team_name ? { ...r, is_hidden: hide } : r));
    } catch {} finally {
      setSavingName(null);
    }
  };

  const hiddenCount = rows.filter(r => r.is_hidden).length;

  return (
    <>
      <div style={{ display: 'flex', gap: '20px', marginBottom: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
        <span><b style={{ color: 'var(--text-primary)' }}>{rows.length}</b> équipes (matchs à venir)</span>
        <span style={{ color: 'var(--red)' }}><b>{hiddenCount}</b> masquées</span>
      </div>

      <div className="filter-search" style={{ marginBottom: '14px', maxWidth: '380px' }}>
        <Search size={14} />
        <input placeholder="Rechercher une équipe…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {error && <ErrorRow msg={error} />}
      {loading ? <Skeleton /> : filtered.length === 0 ? (
        <Empty msg="Aucune équipe" />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {filtered.map((t, idx) => (
            <div
              key={t.team_name}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 16px',
                borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                opacity: t.is_hidden ? 0.55 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{t.team_name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {t.sport ? `${t.sport} · ` : ''}{t.match_count} match{t.match_count > 1 ? 's' : ''} à venir
                </div>
              </div>
              <Toggle
                value={!t.is_hidden}
                disabled={savingName === t.team_name}
                onChange={() => handleToggle(t)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Helpers UI ───
function Toggle({ value, onChange, disabled }: { value: boolean; onChange: () => void; disabled: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange()}
      disabled={disabled}
      title={value ? 'Visible — clique pour masquer' : 'Masqué — clique pour afficher'}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '6px 12px',
        borderRadius: '999px',
        border: '1px solid ' + (value ? 'var(--green)' : 'var(--red)'),
        background: value ? 'rgba(63,185,80,0.10)' : 'rgba(248,81,73,0.10)',
        color: value ? 'var(--green)' : 'var(--red)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontSize: '11px', fontWeight: 700,
        minWidth: '90px', justifyContent: 'center',
      }}
    >
      {value ? <Eye size={12} /> : <EyeOff size={12} />}
      {value ? 'Visible' : 'Masqué'}
    </button>
  );
}

function Skeleton() {
  return <div className="skeleton-list">{[1, 2, 3, 4].map(i => <div key={i} className="skeleton-row" />)}</div>;
}
function Empty({ msg }: { msg: string }) {
  return <div className="card" style={{ padding: '40px', textAlign: 'center' }}><p className="muted">{msg}</p></div>;
}
function ErrorRow({ msg }: { msg: string }) {
  return (
    <div className="card" style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--red)', marginBottom: '12px' }}>
      <AlertCircle size={18} />
      <span>{msg}</span>
    </div>
  );
}
